import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyEmail } from '../services/api';
import './VerifyEmail.css';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage(t('auth.verification.noToken', 'Invalid verification link'));
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const { data } = await verifyEmail(token);
        if (cancelled) return;
        setStatus('success');
        setMessage(data.message);
        setEmail(data.email || '');
        setTimeout(() => {
          if (!cancelled) navigate('/login', { state: { message: t('auth.verification.successRedirect', 'Email verified! Please log in.'), email: data.email } });
        }, 3000);
      } catch (err) {
        if (cancelled) return;
        const d = err.response?.data;
        if (d?.expired) {
          setStatus('expired');
          setMessage(t('auth.verification.expired', 'Verification link has expired'));
        } else if (d?.alreadyVerified) {
          setStatus('success');
          setMessage(t('auth.verification.alreadyVerified', 'Email already verified'));
          setTimeout(() => navigate('/login'), 2000);
        } else {
          setStatus('error');
          setMessage(d?.error || t('auth.verification.failed', 'Verification failed'));
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [searchParams, navigate, t]);

  return (
    <div className="verify-email-page fade-in">
      <div className="verify-email-container">
        {status === 'verifying' && (
          <div className="verify-status verifying">
            <div className="spinner" />
            <h2>{t('auth.verification.verifying', 'Verifying your email...')}</h2>
            <p>{t('auth.verification.pleaseWait', 'Please wait a moment')}</p>
          </div>
        )}
        {status === 'success' && (
          <div className="verify-status success">
            <div className="success-icon">✓</div>
            <h2>{t('auth.verification.success', 'Email Verified!')}</h2>
            <p>{message}</p>
            <p className="redirect-message">{t('auth.verification.redirecting', 'Redirecting to login...')}</p>
            <Link to="/login" className="btn btn-primary btn-lg">{t('auth.verification.loginNow', 'Log In Now')}</Link>
          </div>
        )}
        {status === 'error' && (
          <div className="verify-status error">
            <div className="error-icon">✗</div>
            <h2>{t('auth.verification.error', 'Verification Failed')}</h2>
            <p>{message}</p>
            <Link to="/register" className="btn btn-secondary btn-lg">{t('auth.verification.backToRegister', 'Back to Registration')}</Link>
          </div>
        )}
        {status === 'expired' && (
          <div className="verify-status expired">
            <div className="expired-icon">⏰</div>
            <h2>{t('auth.verification.linkExpired', 'Link Expired')}</h2>
            <p>{message}</p>
            <Link to="/resend-verification" className="btn btn-primary btn-lg">{t('auth.verification.resendLink', 'Resend Verification Email')}</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
